import type { EffectVocabulary, Resource } from '@rollcall/contracts'
import { useT } from './i18n-context.js'

/**
 * Счётчики ресурсов в редакторе контента (D53, D56).
 *
 * Тем же способом, что и эффекты: свой вид поля формы, четыре
 * выбора, и словарь величин — из движка, а не из списка в компоненте
 * (`effectVocabulary`). Ссылка на величину берётся из того же набора
 * ссылок, что и у эффектов: величина, на которую можно сослаться
 * в эффекте, годится и в максимум счётчика.
 *
 * Схему полей форма при этом не спрашивает: обход разметил бы
 * максимум размеченным объединением, и мастер выбирал бы «kind: ref»
 * вместо «столько же, сколько бонус мастерства».
 */

export type ResourceEditorProps = {
  readonly resources: readonly Resource[]
  readonly vocabulary: EffectVocabulary
  readonly onChange: (next: readonly Resource[]) => void
}

/** Ключ из порядкового номера: человеку он не показывается. */
function keyFor(taken: readonly Resource[]): string {
  let number = taken.length + 1
  const used = new Set(taken.map((item) => item.key))
  while (used.has(`resource_${number}`)) number += 1
  return `resource_${number}`
}

const EMPTY: Omit<Resource, 'key'> = {
  name: '',
  max: { kind: 'number', value: 1 },
  recovery: { kind: 'long_rest' },
}

export function ResourceEditor({
  resources,
  vocabulary,
  onChange,
}: ResourceEditorProps) {
  const t = useT()

  const patch = (index: number, next: Resource) => {
    onChange(resources.map((item, at) => (at === index ? next : item)))
  }

  return (
    <div className="app-effects" data-testid="resource-editor">
      {resources.map((resource, index) => (
        <div key={resource.key} className="app-effect">
          <label className="app-field">
            <span>{t('resources.name')}</span>
            <input
              type="text"
              value={resource.name}
              maxLength={60}
              onChange={(event) => {
                patch(index, { ...resource, name: event.target.value })
              }}
              data-testid={`resource-editor-name-${index}`}
            />
          </label>

          <label className="app-field">
            <span>{t('resources.max')}</span>
            <select
              value={resource.max.kind === 'number' ? '' : resource.max.path}
              onChange={(event) => {
                const path = event.target.value
                patch(index, {
                  ...resource,
                  max:
                    path === ''
                      ? { kind: 'number', value: 1 }
                      : { kind: 'ref', path, min: null },
                })
              }}
              data-testid={`resource-editor-max-${index}`}
            >
              <option value="">{t('resources.maxNumber')}</option>
              {vocabulary.refs.map((ref) => (
                <option key={ref.path} value={ref.path}>
                  {ref.path}
                </option>
              ))}
            </select>
          </label>

          {resource.max.kind === 'number' ? (
            <label className="app-field">
              <span>{t('resources.maxValue')}</span>
              <input
                type="number"
                min={1}
                max={999}
                value={resource.max.value}
                onChange={(event) => {
                  const amount = Number.parseInt(event.target.value, 10)
                  patch(index, {
                    ...resource,
                    max: {
                      kind: 'number',
                      value: Number.isFinite(amount) && amount > 0 ? amount : 1,
                    },
                  })
                }}
                data-testid={`resource-editor-value-${index}`}
              />
            </label>
          ) : (
            <label className="app-field">
              <span>{t('resources.min')}</span>
              <input
                type="number"
                min={0}
                max={99}
                value={resource.max.min ?? 0}
                onChange={(event) => {
                  const floor = Number.parseInt(event.target.value, 10)
                  const path = resource.max.kind === 'ref' ? resource.max.path : ''
                  patch(index, {
                    ...resource,
                    max: {
                      kind: 'ref',
                      path,
                      min: Number.isFinite(floor) && floor > 0 ? floor : null,
                    },
                  })
                }}
                data-testid={`resource-editor-min-${index}`}
              />
            </label>
          )}

          <label className="app-field">
            <span>{t('resources.recoveryLabel')}</span>
            <select
              value={resource.recovery.kind}
              onChange={(event) => {
                const kind = event.target.value
                patch(index, {
                  ...resource,
                  recovery:
                    kind === 'short_rest'
                      ? { kind: 'short_rest', uses: null }
                      : kind === 'long_rest'
                        ? { kind: 'long_rest' }
                        : { kind: 'none' },
                })
              }}
              data-testid={`resource-editor-recovery-${index}`}
            >
              <option value="long_rest">{t('resources.recovery.long')}</option>
              <option value="short_rest">{t('resources.recovery.short')}</option>
              <option value="none">{t('resources.recovery.none')}</option>
            </select>
          </label>

          <div className="app-actions">
            <button
              type="button"
              className="app-button app-button-quiet"
              onClick={() => {
                onChange(resources.filter((_, at) => at !== index))
              }}
              data-testid={`resource-editor-remove-${index}`}
            >
              {t('resources.remove')}
            </button>
          </div>
        </div>
      ))}

      <div className="app-actions">
        <button
          type="button"
          className="app-button"
          onClick={() => {
            onChange([...resources, { key: keyFor(resources), ...EMPTY }])
          }}
          data-testid="resource-editor-add"
        >
          {t('resources.addOpen')}
        </button>
      </div>
    </div>
  )
}
